import React, { useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import "./App.css";
import "./i18n";
import { useTranslation } from "react-i18next";
import ApproveRejectCerificate from "./Pages/ApproveRejectCerificate";
import DownloadDuplicateCertifcate from "./Pages/DownloadDuplicateCertifcate";

// Eager-load only the home page — everything else is lazy

const Home            = lazy(() => import("./Pages/Home"));
const About            = lazy(() => import("./Pages/About"));
const Contact          = lazy(() => import("./Pages/Contact"));
const PresidentMessage = lazy(() => import("./Pages/PresidentMessage"));
const NewsPage         = lazy(() => import("./Pages/NewsPage"));
const Gallery          = lazy(() => import("./Pages/Gallery"));
const SocialResponsibility = lazy(() => import("./Pages/Social_Responsibility"));
const ImportantLinks   = lazy(() => import("./Pages/ImportantLinks"));
const WhyJoinAiraa     = lazy(() => import("./Pages/WhyJoinAiraa"));
const Member_Services  = lazy(() => import("./Pages/Member_Services"));
const Helpline         = lazy(() => import("./Pages/Helpline"));
const NoticesAndEvents = lazy(() => import("./Pages/NoticesAndEvents"));
const Certificate      = lazy(() => import("./Pages/Certificate"));
const Members          = lazy(() => import("./Pages/Members"));
const AdminDashboard   = lazy(() => import("./Pages/Admin-Dashboard"));
const RegistrationFree = lazy(() => import("./Pages/RegistrationFree"));
const MembershipFree   = lazy(() => import("./Pages/MembershipFree"));
const MembershipPayment= lazy(() => import("./Pages/MembershipPayment"));
const BulkEmailSender= lazy(() => import("./Pages/BulkEmailSender"));
const NotFound         = lazy(() => import("./Pages/NotFound"));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="w-10 h-10 rounded-full border-4 border-blue-600 border-t-transparent animate-spin" />
  </div>
);

const pageVariants = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.38, ease: [0.25, 0.46, 0.45, 0.94] } },
  exit: { opacity: 0, y: -10, transition: { duration: 0.22, ease: "easeIn" } },
};

function AnimatedRoutes() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        <Suspense fallback={<PageLoader />}>
          <Routes location={location}>
            <Route path="/" element={<Home />} />
            <Route path="/about-airaa" element={<About />} />
            <Route path="/contact-us" element={<Contact />} />
            <Route path="/presidentmessage" element={<PresidentMessage />} />
            <Route path="/news" element={<NewsPage />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/socialresponsibility" element={<SocialResponsibility />} />
            <Route path="/importantlinks" element={<ImportantLinks />} />
            <Route path="/why-join-airaa" element={<WhyJoinAiraa />} />
            <Route path="/member-services" element={<Member_Services />} />
            <Route path="/registration" element={<RegistrationFree />} />
            <Route path="/membership" element={<MembershipFree />} />
            <Route path="/helpline" element={<Helpline />} />
            {/* <Route path="/notices" element={<NoticesAndEvents />} /> */}
            <Route path="/certificate" element={<Certificate />} />
            <Route path="/members" element={<Members />} />
            <Route path="/admin-dashboard" element={<AdminDashboard />} />
            <Route path="/bulk-email-sender" element={<BulkEmailSender />} />
            <Route path="/approve-reject-certificate" element={<ApproveRejectCerificate />} />
            <Route path="/download-duplicate-certificate" element={<DownloadDuplicateCertifcate />} />

            {/* Legacy / old-site redirects — 301 equivalent on the client side */}
            <Route path="/Default.aspx" element={<Navigate to="/" replace />} />
            <Route path="/gallary" element={<Navigate to="/gallery" replace />} />
            <Route path="/Social-Responsibility.aspx" element={<Navigate to="/socialresponsibility" replace />} />

            {/* Catch-all — must be last */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
}

function App() {
  const { i18n } = useTranslation();
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    const selectedLanguage = localStorage.getItem("selectedLanguage");
    if (!selectedLanguage) {
      setShowPopup(true);
    } else {
      i18n.changeLanguage(selectedLanguage);
    }
  }, [i18n]);

  const handleLanguageSelect = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("selectedLanguage", lang);
    setShowPopup(false);
  };

  return (
    <Router>
      <AnimatedRoutes />
    </Router>
  );
}

export default App;
